import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import * as d3 from "d3";
import clsx from "clsx";
import "./ScrollablePPGWaveform.scss";
import { useDragToScroll } from "../../../hooks/useDragToScroll";

interface StaticScrollingPPGWaveformChartProps {
    className?: string;
    data: number[];
    inPrintMode?: boolean;
    pixelsPerSecond?: number;
    chartHeight?: number;
    verticalGridLines?: number;
    lineStrokeWidth?: number;
}

//const samplesPerSecond = 30; // constant

const StaticScrollingPPGWaveformChart: React.FC<
    StaticScrollingPPGWaveformChartProps
> = ({
    className = "",
    data,
    pixelsPerSecond = 100,
    chartHeight = 200,
    verticalGridLines = 3,
    lineStrokeWidth = 4,
}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const { ref: containerRef, isDragging } = useDragToScroll();

    /// geometry
    const samplesPerSecond = 30;
    const tickCount = Math.ceil(data.length / samplesPerSecond);
    const width = tickCount * pixelsPerSecond;
    const height = chartHeight;

    //ui state
    const [fitsContainer, setFitsContainer] = useState(false);
    const [showLeftShadow, setShowLeftShadow] = useState(false);
    const [showRightShadow, setShowRightShadow] = useState(false);

    const updateFlags = useCallback(() => {
        const el = containerRef.current;
        const sg = svgRef.current;
        if (!el || !sg) return;

        const hasScroll = el.scrollWidth > el.clientWidth + 1;
        const atLeft = el.scrollLeft <= 1;
        const atRight = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

        setShowLeftShadow(hasScroll && !atLeft);
        setShowRightShadow(hasScroll && !atRight);
        el.classList.toggle("show-shadow", hasScroll && !atRight);

        setFitsContainer(
            sg.getBoundingClientRect().width <=
                el.getBoundingClientRect().width + 0.5,
        );
    }, []);

    // draw once
    useLayoutEffect(() => {
        if (!data.length || !svgRef.current || !containerRef.current) return;

        const svg = d3
            .select(svgRef.current)
            .attr("width", width)
            .attr("height", height);
        svg.selectAll("*").remove();

        const margin = { top: 10, right: 10, bottom: 24, left: 10 };
        const innerW = width - margin.left - margin.right;
        const innerH = height - margin.top - margin.bottom;

        const g = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        const x = d3
            .scaleLinear()
            .domain([0, data.length - 1])
            .range([0, innerW]);

        const y = d3
            .scaleLinear()
            .domain(d3.extent(data) as [number, number])
            .range([innerH, 0]);

        // major / minor vertical grid lines
        const majors = d3.range(0, data.length, samplesPerSecond);

        g.selectAll("vgrid")
            .data(majors)
            .enter()
            .append("line")
            .attr("x1", (d) => x(d))
            .attr("x2", (d) => x(d))
            .attr("y1", 0)
            .attr("y2", innerH)
            .attr("stroke", "#ccc")
            .attr("stroke-width", 1);

        majors.slice(0, -1).forEach((start, i) => {
            const end = majors[i + 1];
            const step = (end - start) / (verticalGridLines + 1);
            for (let j = 1; j <= verticalGridLines; j++) {
                const pos = start + j * step;
                g.append("line")
                    .attr("x1", x(pos))
                    .attr("x2", x(pos))
                    .attr("y1", 0)
                    .attr("y2", innerH)
                    .attr("stroke", "#eee")
                    .attr("stroke-width", 1);
            }
        });

        // horizontal grid
        const squares = tickCount * (verticalGridLines + 1);
        const squareW = innerW / squares;
        const rows = Math.floor(innerH / squareW);
        for (let i = 0; i <= rows; i++) {
            const yPos = innerH - i * squareW;
            g.append("line")
                .attr("x1", 0)
                .attr("x2", innerW)
                .attr("y1", yPos)
                .attr("y2", yPos)
                .attr("stroke", "#eee")
                .attr("stroke-width", 1);
        }

        // axis WITH tick marks
        g.append("g")
            .attr("transform", `translate(0,${innerH})`)
            .call(
                d3
                    .axisBottom(x)
                    .tickValues(majors)
                    .tickFormat((d) => `${(+d / samplesPerSecond).toFixed(0)}s`)
                    .tickSize(6)
                    .tickSizeOuter(0),
            )
            .call((g) => g.select(".domain").attr("stroke", "#000"))
            .call((g) =>
                g
                    .selectAll("text")
                    .attr("font-size", 10)
                    .attr("fill", "#555")
                    .style("user-select", "none")
                    .attr("pointer-events", "none"),
            );

        // waveform
        g.append("path")
            .datum(data)
            .attr("fill", "none")
            .attr("stroke", "#000")
            .attr("stroke-width", lineStrokeWidth)
            .attr(
                "d",
                d3
                    .line<number>()
                    .x((_, i) => x(i))
                    .y((d) => y(d))
                    .curve(d3.curveLinear),
            );

        // initial flags
        updateFlags();
    }, [data, width, height, verticalGridLines, lineStrokeWidth, updateFlags]);

    // observer for scrolling
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        el.addEventListener("scroll", updateFlags, { passive: true });
        const ro = new ResizeObserver(updateFlags);
        ro.observe(el);
        return () => {
            el.removeEventListener("scroll", updateFlags);
            ro.disconnect();
        };
    }, [updateFlags]);

    return (
        <div className="ScrollablePPGWaveformWrap">
            <div
                ref={containerRef}
                className={clsx(
                    "ScrollablePPGWaveform",
                    className,
                    { "is-dragging": isDragging },
                    { "fits-container": fitsContainer },
                )}
            >
                <svg
                    ref={svgRef}
                    className="ScrollablePPGWaveformSvg"
                    width={width}
                    height={height}
                />
            </div>
            {showLeftShadow && <div className="scroll-shadow-left" />}
            {showRightShadow && <div className="scroll-shadow-right" />}
            <div className="scroll-shadow" /> {/* legacy overlay */}
        </div>
    );
};
export default StaticScrollingPPGWaveformChart;
